import React, { useEffect, useState, useRef, useCallback } from "react";
import "./Question.css";
import RichQuestionEditor from "./RichQuestionEditor";
import {
  getInputSpeechId,
  generateRandomString,
} from "../../../../../../../core/common-service/CommonService";
import { postGetGoogleSpeechToText } from "../../../../../../../core/api/Api";
import { MAX_AUDIO_LENGTH } from "../../../../../../../core/constants/constants";
import { useAudioRecorder } from "react-audio-voice-recorder";
import { useTranslation } from "react-i18next";
import Tooltip from "../../../../../../_components/tooltip/Tooltip";
import formatNumber from "../../../../../../../core/utils/formatNumber";
import useIsMobile from "../../../../../../../core/hooks/useMobile";
import { svgs } from "../../../../../../../core/constants/svgs";
import { showToast } from "../../../../../../../core/common-service/CommonService";
import { useQuestionsContext } from "../../context/QuestionsContext";
import recordIcon from "../../../../../../../assets/png/record.png";
import rephraseIcon from "../../../../../../../assets/png/rephrase.png";
import addIcon from "../../../../../../../assets/png/add.png";
import editIcon from "../../../../../../../assets/png/edit.png";
import deleteIcon from "../../../../../../../assets/png/delete.png";
import checkIcon from "../../../../../../../assets/png/check.png";

const Question = ({
  question,
  onResult,
  onEditDeleteQuestion,
  showSideOption = true,
  onDragStart,
  onDragEnter,
  onDragEnd,
  draggable,
  onUpdateQuestion,
  onAddQuestion,
  isNewQuestion = false,
  labelOverride,
  showAnswerHint = false,
}) => {
  const { isPaid, category_based_type_id, category_length_type_id, isSelfWrite } = useQuestionsContext();
  const [recordLoading, setRecordLoading] = useState(false);
  const [textLength, setTextLength] = useState(0);
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  // Local state for true inline title editing
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");

  const richEditorRef = useRef(null);
  const titleInputRef = useRef(null);
  const onResultRef = useRef(onResult);

  const {
    startRecording,
    stopRecording,
    recordingBlob,
    isRecording,
    recordingTime,
  } = useAudioRecorder();

  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);

  // Initialize text length when transcript loads
  useEffect(() => {
    const transcript = question?.transcript ?? "";
    setTextLength(transcript.length);
  }, [question?.transcript]);

  // Auto-open edit mode for new questions - Enhanced with debugging
  useEffect(() => {
    if (isNewQuestion) {
      setEditedTitle(question?.name || "");
      setIsEditingTitle(true);

      // Focus the input after a short delay to ensure it's rendered
      setTimeout(() => {
        if (titleInputRef.current) {
          titleInputRef.current.focus();
        }
      }, 100);
    }
  }, [isNewQuestion, question?.name]);

  const padValue = (value) => (value < 10 ? `0${value}` : value);

  const setTimerHtml = (audioRecorderTimer) => {
    const remainingSeconds = MAX_AUDIO_LENGTH - audioRecorderTimer;
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;
    return `${padValue(minutes)}:${padValue(seconds)}`;
  };

  const onInit = useRef(true);
  useEffect(() => {
    if (!onInit.current) return;
    onInit.current = false;
    if (recordingTime > MAX_AUDIO_LENGTH) {
      stopRecording();
    }
  }, [recordingTime, stopRecording]);

  const uploadRecordingBlob = useCallback((blob) => {
    const formData = new FormData();
    const audio_name =
      "audio_" + generateRandomString() + "_" + Date.now() + ".webm";
    formData.append("audio", blob, audio_name);

    setRecordLoading(true);

    postGetGoogleSpeechToText(formData)
      .then((res) => {
        setRecordLoading(false);
        try {
          const googleTranscript = res?.data?.success?.transcript;
          if (googleTranscript && richEditorRef.current) {
            const currentText = richEditorRef.current.getPlainText();
            const prefix = currentText.trim().length > 0 ? ", " : "";
            richEditorRef.current.insertText(prefix + googleTranscript);
            // onUpdate inside RichQuestionEditor will call onChange → onResult automatically
          }
        } catch (error) {
          setRecordLoading(false);
        }
      })
      .catch(() => {
        setRecordLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!recordingBlob) return;
    uploadRecordingBlob(recordingBlob);
  }, [recordingBlob, uploadRecordingBlob]);

  const stopRecordingFunction = async () => {
    await stopRecording();
  };

  // True inline title editing handlers
  const handleEditQuestion = (e) => {
    e.stopPropagation();
    setEditedTitle(question?.name || "");
    setIsEditingTitle(true);
  };

  const handleTitleChange = (e) => {
    setEditedTitle(e.target.value);
  };

  const handleTitleSubmit = () => {
    if (!editedTitle.trim()) {
      showToast({
        type: "error",
        message: t("please_add_question_title") || "Please add question title",
      });
      return;
    }

    const questionData = {
      ...question,
      name: editedTitle,
      category_based_type_id,
      category_length_type_id,
    };

    if (onUpdateQuestion) {
      onUpdateQuestion(questionData);
    }

    setIsEditingTitle(false);
  };

  const handleTitleCancel = () => {
    setIsEditingTitle(false);
    setEditedTitle("");

    // If this is a new question and user cancels, delete it
    if (isNewQuestion && onEditDeleteQuestion) {
      // Create a mock event object to prevent the error
      const mockEvent = {
        preventDefault: () => {},
        stopPropagation: () => {},
      };
      onEditDeleteQuestion("delete")(mockEvent);
    }
  };

  const handleAddQuestion = (e) => {
    e.stopPropagation();
    if (onAddQuestion) {
      onAddQuestion();
    }
  };

  // Self-write mode: questions are shown as read-only writing prompts only
  if (isSelfWrite) {
    return (
      <div className={`${isMobile ? "mt-2" : "mt-4"}`}>
        <div className="aai-comment-input mb-3">
          <label className="aai-comment-form-label label-flex-updated">
            <div className="left" style={{ width: "100%" }}>
              <span className="question">{question?.name}</span>
            </div>
          </label>
        </div>
      </div>
    );
  }

  return (
    <div className={`${isMobile ? "mt-2" : "mt-4"}`}>
      <div className="aai-comment-input mb-3">
        <label className="aai-comment-form-label label-flex-updated">
          <div className="left" style={{ width: "100%" }}>
            {/* {!isMobile && (
              <img
                src={svgs.draggable}
                width={24}
                alt="draggable svg"
                className="question-draggable-icon"
                onDragStart={!isEditingTitle ? onDragStart : undefined}
                onDragEnter={!isEditingTitle ? onDragEnter : undefined}
                onDragEnd={!isEditingTitle ? onDragEnd : undefined}
                onDragOver={(e) => e.preventDefault()}
                draggable={draggable && !isEditingTitle}
                style={{
                  cursor: draggable && !isEditingTitle ? "move" : "default",
                  marginRight: "12px",
                  flexShrink: 0,
                  pointerEvents: "auto",
                }}
              />
            )} */}

            {/* True Inline Title Editing */}
            {isEditingTitle ? (
              <div
                className="d-flex align-items-center"
                style={{
                  flex: 1,
                  width: "100%",
                  gap: "8px",
                }}
              >
                <input
                  ref={titleInputRef}
                  type="text"
                  className="form-control"
                  value={editedTitle}
                  onChange={handleTitleChange}
                  onBlur={handleTitleSubmit}
                  placeholder={t("enter_question_title")}
                  autoFocus
                  style={{
                    border: isNewQuestion
                      ? "2px solid #c47a26"
                      : "1px solid #c47a26",
                    borderRadius: "4px",
                    padding: "6px 10px",
                    fontSize: "inherit",
                    fontFamily: "inherit",
                    flex: 1,
                    width: "100%",
                    backgroundColor: isNewQuestion ? "#fffcf8ff" : "white",
                  }}
                />
                <button
                  className="btn btn-sm"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleTitleSubmit();
                  }}
                  style={{
                    border: "none",
                    borderRadius: "50%",
                    width: "24px",
                    height: "24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0,
                    fontSize: "12px",
                  }}
                >
                  <img src={checkIcon} alt="Check" width={24} height={24} />
                </button>
              </div>
            ) : (
              <span
                className={`question ${isNewQuestion ? "text-muted" : ""}`}
                style={{
                  fontStyle:
                    isNewQuestion && !question?.name ? "italic" : "normal",
                  color: isNewQuestion && !question?.name ? "#999" : "inherit",
                  userSelect: "text",
                  cursor: "text",
                  pointerEvents: "auto",
                }}
                onMouseDown={(e) => {
                  // Stop propagation to prevent any drag behavior
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  // Stop propagation to prevent any click handlers
                  e.stopPropagation();
                }}
                onDragStart={(e) => {
                  // Prevent this element from being draggable
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                {labelOverride ||
                  question?.name ||
                  (isNewQuestion
                    ? t("new_question") || "New Question - Click to edit"
                    : "")}
              </span>
            )}
          </div>

          {/* Action Buttons - Hide during title editing */}
          {!isEditingTitle && (
            <>
              {/* New Action Buttons for Inline Editing */}
              {isPaid && onUpdateQuestion && (
                <div
                  className="d-flex align-items-center"
                  style={{ gap: "8px", marginLeft: "10px" }}
                >
                  {/* Edit Button */}
                  <button
                    className="btn btn-sm"
                    onClick={handleEditQuestion}
                    style={{
                      backgroundColor: "transparent",
                      borderRadius: "50%",
                      width: "20px",
                      height: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#666",
                      fontSize: "10px",
                    }}
                    title={t("edit_question")}
                  >
                    <img src={editIcon} alt="Edit" width={20} height={20} />
                  </button>

                  {/* Add Question Button */}
                  <button
                    className="btn btn-sm"
                    onClick={handleAddQuestion}
                    style={{
                      backgroundColor: "transparent",
                      borderRadius: "50%",
                      width: "20px",
                      height: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#666",
                      fontSize: "10px",
                    }}
                    title={t("add_question")}
                  >
                    <img src={addIcon} alt="Add" width={20} height={20} />
                  </button>
                </div>
              )}

              {recordingTime > 0 && (
                <div className="recording-timer">
                  {setTimerHtml(recordingTime)}
                </div>
              )}
            </>
          )}
        </label>

        {/* ==== Main Row: Left rail + Textarea + Right actions ==== */}
        <div className="textarea-container">
          {/* LEFT RAIL: mic at top, magic above delete at bottom-left */}
          <div className="left-rail">
            <div className="left-rail-top">
              <Tooltip text={t("speech_to_text")}>
                <button
                  className={`question_record_button_new btn ${
                    isRecording ? "recording-audio" : ""
                  }`}
                  onClick={isRecording ? stopRecordingFunction : startRecording}
                  disabled={recordLoading || isEditingTitle}
                >
                  {recordLoading ? (
                    <div className="spinner-icon">
                      <i className="fa-solid fa-spinner fa-spin" />
                    </div>
                  ) : (
                    <img
                      src={recordIcon}
                      alt="Record"
                      width={50}
                      height={50}
                      className={isRecording ? "recording-pulse" : ""}
                    />
                  )}
                </button>
              </Tooltip>
              {showSideOption && (
                <Tooltip text={t("magic_rephrase")}>
                  <button
                    className="aai-breadcrumb-link text-decoration-underline"
                    onClick={onEditDeleteQuestion("optimize")}
                    disabled={isEditingTitle}
                  >
                    <img
                      id={getInputSpeechId(question) + "_optimize_icon"}
                      src={rephraseIcon}
                      alt="Rephrase"
                      width={50}
                      height={50}
                      className="icons"
                    />
                  </button>
                </Tooltip>
              )}
            </div>

            {showSideOption && (
              <div className="left-rail-bottom">
                <Tooltip text={t("delete_question")}>
                  <button
                    className="aai-breadcrumb-link text-decoration-underline"
                    onClick={onEditDeleteQuestion("delete")}
                    disabled={isEditingTitle}
                  >
                    <img
                      src={deleteIcon}
                      alt="Delete"
                      width={50}
                      height={50}
                      className="icons"
                    />
                  </button>
                </Tooltip>
              </div>
            )}
          </div>

          {/* RICH TEXT EDITOR */}
          <div className="textarea-wrapper" style={{ position: "relative" }}>
            <RichQuestionEditor
              ref={richEditorRef}
              id={getInputSpeechId(question)}
              value={question?.transcript ?? ""}
              onChange={(newVal) => {
                onResult(newVal);
              }}
              onCharCount={(count) => setTextLength(count)}
              disabled={recordLoading || isEditingTitle}
              maxLength={2000}
            />
            <div className={`rich-question-char-count ${textLength >= 2000 ? "rich-question-char-count--warning" : ""}`}>
              {formatNumber(textLength)} {t("character")}
              {textLength > 1 ? "s" : ""}
            </div>
          </div>

          {/* RIGHT ACTIONS: keep Edit on the right (original functionality) */}
          {showSideOption && !onUpdateQuestion && (
            <div className="right-actions">
              <Tooltip text={t("edit_question")}>
                <button
                  className="aai-breadcrumb-link text-decoration-underline"
                  onClick={onEditDeleteQuestion("edit")}
                  disabled={isEditingTitle}
                >
                  <img
                    src={editIcon}
                    alt="Edit"
                    width={22}
                    height={22}
                    className="icons"
                  />
                </button>
              </Tooltip>
            </div>
          )}
        </div>
        {showAnswerHint && (
          <p
            className="question-answer-hint"
            style={{ fontSize: "13px", color: "#6c757d", margin: "8px 0 0 0" }}
          >
            {t("answer_voice_rephrase_hint")}
          </p>
        )}
      </div>
    </div>
  );
};

export default Question;
